<template>
  <el-table v-sticky-header stripe fit highlight-current-row :data="list" header-cell-class-name="table-header-cell">
    <el-table-column label="订单号" prop="orderNumber" width="120" align="center" :fixed="device !== 'mobile'" />

    <template v-if="isRefund">
      <el-table-column label="退款单号" prop="refundNumber" width="120" align="center" />
      <el-table-column label="退款时间" prop="refundTime" width="100" align="center">
        <template slot-scope="{row}">
          <div>{{ row.refundTime | filterDate }}</div>
          <div>{{ row.refundTime | filterTime }}</div>
        </template>
      </el-table-column>
    </template>
    <el-table-column label="支付单号" prop="payNumber" width="120" align="center" />
    <el-table-column label="下单时间" prop="orderTime" width="100" align="center">
      <template slot-scope="{row}">
        <div>{{ row.orderTime | filterDate }}</div>
        <div>{{ row.orderTime | filterTime }}</div>
      </template>
    </el-table-column>

    <el-table-column label="用户信息" min-width="150">
      <template slot-scope="{row}">
        <div><span class="bold">编号：</span>{{ row.shareCode }}</div>
        <div><span class="bold">昵称：</span>{{ row.nickName }}</div>
        <div><span class="bold">手机：</span>{{ row.mobile }}</div>
      </template>
    </el-table-column>
    <el-table-column label="商品信息" min-width="250">
      <template slot-scope="{row}">
        <div v-for="(item, pid) in row.products" :key="pid" class="flex align-start just-between">
          <el-image class="product-cover" :src="item.cover" :preview-src-list="[item.cover]" lazy />
          <div class="flex-sub">{{ item.goodsName }}</div>
          <div class="sku-count">{{ `x${item.skuCount}` }}</div>
        </div>
      </template>
    </el-table-column>

    <template v-if="!isRefund">
      <el-table-column label="配送方式" prop="deliveryMethodName" width="80" align="center" />
      <el-table-column v-if="isLocal" label="商家信息" min-width="200">
        <template slot-scope="{row}">
          <div><span class="bold">{{ row.consigneeName }}</span></div>
          <div>{{ row.consigneeMobile }}</div>
          <div>{{ `${row.consigneeRegion} ${row.consigneeAddress}` }}</div>
        </template>
      </el-table-column>
      <el-table-column v-else label="收货信息" min-width="200">
        <template slot-scope="{row}">
          <div>
            <span class="bold">{{ row.consigneeName }}</span>
            <span class="pl-10">{{ row.consigneeMobile }}</span>
          </div>
          <div>{{ `${row.consigneeRegion}${row.consigneeAddress}` }}</div>
          <div v-if="row.logisticsName" class="mt-5"><span class="bold">快递公司：</span>{{ row.logisticsName }}</div>
          <div v-if="row.logisticsNumber"><span class="bold">快递单号：</span>{{ row.logisticsNumber }}</div>
          <div v-if="row.logisticsTime"><span class="bold">发货时间：</span>{{ row.logisticsTime }}</div>
        </template>
      </el-table-column>
    </template>

    <el-table-column label="订单金额" prop="orderPrice" min-width="90" align="right">
      <template slot-scope="{row}">
        <template v-if="row.orderType === 72000">
          <div class="space-nowrap"><span class="bold">总金额：</span><span>{{ row.orderPrice | filterInteger }}</span></div>
          <div class="space-nowrap"><span class="bold">积分：</span><span>{{ row.orderPoint | filterInteger }}</span></div>
          <div class="space-nowrap"><span class="bold">券：</span><span>{{ row.orderCoupon | filterInteger }}</span></div>
        </template>
        <template v-else-if="row.orderType === 73000">
          <span class="bold">积分：</span>{{ row.orderPoint | filterInteger }}
        </template>
        <template v-else-if="row.orderType === 75000">
          <div class="space-nowrap"><span class="bold">现金：</span><span>{{ row.orderPrice | filterPrice }}</span></div>
          <div v-if="parseFloat(row.orderPoint) > 0" class="space-nowrap"><span class="bold">积分：</span><span>{{ row.orderPoint | filterPrice }}</span></div>
          <div v-if="parseFloat(row.orderCoupon) > 0" class="space-nowrap"><span class="bold">券：</span><span>{{ row.orderCoupon | filterPrice }}</span></div>
        </template>
        <template v-else-if="row.orderType === 76000">
          <div class="space-nowrap"><span class="bold">积分：</span><span>{{ row.orderPoint | filterInteger }}</span></div>
          <div class="space-nowrap"><span class="bold">券：</span><span>{{ row.orderCoupon | filterInteger }}</span></div>
        </template>
        <template v-else-if="row.orderType === 77000">
          <template v-if="row.payMethod === 0">
            <span class="bold">积分：</span>{{ row.orderPoint | filterInteger }}
          </template>
          <template v-else>
            <div class="space-nowrap"><span class="bold">现金：</span><span>{{ row.orderPrice | filterPrice }}</span></div>
            <div v-if="row.payMethod === 2" class="space-nowrap"><span class="bold">积分：</span><span>{{ row.orderPoint | filterPrice }}</span></div>
          </template>
        </template>
      </template>
    </el-table-column>

    <el-table-column label="状态" prop="orderStatusName" width="80" align="center">
      <template slot-scope="{row}">
        <template v-if="isRefund">
          <el-tag v-if="row.refundStatus === 4" type="info" size="medium" effect="dark">{{ getRefundStatus(row.refundStatus) }}</el-tag>
          <el-tag v-else-if="row.refundStatus === 3" type="success" size="medium" effect="dark">{{ getRefundStatus(row.refundStatus) }}</el-tag>
          <el-tag v-else-if="row.refundStatus === 2" type="warning" size="medium" effect="dark">{{ getRefundStatus(row.refundStatus) }}</el-tag>
          <el-tag v-else-if="row.refundStatus === 1" type="danger" size="medium" effect="dark">{{ getRefundStatus(row.refundStatus) }}</el-tag>
        </template>
        <template v-else>
          <el-tag v-if="row.orderStatus >= 51" type="success" size="medium" effect="dark">{{ row.orderStatusName }}</el-tag>
          <el-tag v-else-if="row.orderStatus === 13" type="info" size="medium" effect="dark">{{ row.orderStatusName }}</el-tag>
          <el-tag v-else-if="row.orderStatus === 41" type="warning" size="medium" effect="dark">{{ row.orderStatusName }}</el-tag>
          <el-tag v-else-if="row.orderStatus === 21" type="danger" size="medium" effect="plain">{{ row.orderStatusName }}</el-tag>
          <el-tag v-else size="medium" effect="dark">{{ row.orderStatusName }}</el-tag>
        </template>
      </template>
    </el-table-column>

    <el-table-column align="center" width="80" fixed="right">
      <template slot-scope="{row}">
        <el-dropdown trigger="click" szie="medium">
          <span class="el-dropdown-link">
            操作<i class="el-icon-arrow-down el-icon--right" />
          </span>
          <el-dropdown-menu slot="dropdown">
            <template v-if="isRefund">
              <el-dropdown-item icon="el-icon-refresh-left" :disabled="row.refundStatus > 1" @click.native.stop="handleVerify(row)">退货退款</el-dropdown-item>
            </template>
            <template v-else>
              <el-dropdown-item v-if="row.deliveryMethod === 1" icon="el-icon-finished" :disabled="!!row.logisticsTime" @click.native.stop="handleVerify(row)">订单核销</el-dropdown-item>
              <template v-if="row.deliveryMethod === 2">
                <el-dropdown-item :disabled="row.orderStatus !== 21" icon="el-icon-upload" @click.native.stop="handleShipping(row)">订单发货</el-dropdown-item>
                <el-dropdown-item :disabled="row.orderStatus !== 21" icon="el-icon-shopping-bag-2" divided @click.native.stop="handlePickup(row)">设置自提</el-dropdown-item>
              </template>
              <template v-if="[71000, 72000, 75000, 77000].includes(row.orderType)">
                <el-dropdown-item divided icon="el-icon-money" @click.native.stop="handleRefund(row)">退货退款</el-dropdown-item>
              </template>
            </template>
            <el-dropdown-item divided icon="el-icon-document" @click.native.stop="handleDetail(row)">订单详情</el-dropdown-item>
            <el-dropdown-item icon="el-icon-camera" @click.native.stop="handleRecord(row)">操作记录</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </template>
    </el-table-column>

    <order-delivery v-if="showDelivery" :show.sync="showDelivery" :order.sync="orderInfo" @refresh="shipmentRefresh" />
    <order-refund v-if="showRefund" :show.sync="showRefund" :order.sync="orderInfo" @refresh="refundRefresh" />
    <order-detail v-if="showDetail" :show.sync="showDetail" :order="orderInfo" />
    <order-log v-if="showLog" :show.sync="showLog" :order-id="orderInfo.id" />
  </el-table>
</template>

<script>
import { mapGetters } from 'vuex'
import { StickyHeader } from '@cell-x/el-table-sticky'
import { orderVerify, resetDeliveryMethod } from '@/api/order'

export default {
  name: 'OrderTable',
  directives: {
    StickyHeader: new StickyHeader({ offsetTop: 0, offsetBottom: 0 }).init()
  },
  emits: ['verify'],
  components: {
    OrderDelivery: () => import('./order-delivery'),
    OrderDetail: () => import('./order-detail'),
    OrderLog: () => import('./order-log'),
    OrderRefund: () => import('./order-refund')
  },
  props: {
    list: {
      type: Array,
      default() {
        return []
      }
    },
    isPremium: Boolean,
    isRefund: Boolean,
    isLocal: Boolean
  },
  data() {
    return {
      showDelivery: false,
      showDetail: false,
      showRefund: false,
      showLog: false,
      orderInfo: {}
    }
  },
  computed: mapGetters(['device']),
  methods: {
    getRefundStatus(status = 0) {
      const statusText = ['申请中', '退款中', '已退款', '已拒绝']
      return statusText[status - 1]
    },
    handleVerify(item) {
      this.$confirm('确定要核销该订单吗？', '温馨提示', { type: 'warning' }).then(() => {
        const data = {
          deliveryMethod: item.deliveryMethod,
          orderNumber: item.orderNumber,
          id: item.id
        }
        orderVerify(data).then(() => {
          this.$message.success('核销成功')
          this.$emit('verify')
        }).catch(() => {})
      }).catch(() => {})
    },
    handleShipping(item) {
      this.showDelivery = true
      this.orderInfo = Object.assign({}, item)
    },
    shipmentRefresh() {
      this.$util.delay().then(() => {
        const index = this.list.findIndex(item => item.id === this.orderInfo.id)
        if (index >= 0) this.list.splice(index, 1, this.orderInfo)
      })
    },
    handlePickup(item) {
      this.$confirm('确定要更改该订单的配送方式为自提？', '温馨提示', { type: 'warning' }).then(() => {
        resetDeliveryMethod(item.orderNumber).then(response => {
          if (response) {
            this.$message.success('成功更改配送方式为自提')
            this.$util.delay().then(() => {
              item.orderStatus = 41
              item.orderStatusName = '待核销'
              item.deliveryMethod = 1
              item.deliveryMethodName = '自提'
            })
          }
        }).catch(() => {})
      }).catch(() => {})
    },
    handleRefund(item) {
      this.showRefund = true
      this.orderInfo = Object.assign({}, item)
    },
    refundRefresh() {
      this.$message.success('申请退货退款成功')
      this.$util.delay().then(() => {
        const index = this.list.findIndex(item => item.id === this.orderInfo.id)
        if (index >= 0) this.list.splice(index, 1, this.orderInfo)
      })
    },
    handleDetail(item) {
      this.showDetail = true
      this.orderInfo = item
    },
    handleRecord(item) {
      this.showLog = true
      this.orderInfo = item
    }
  }
}
</script>

<style lang="scss" scoped>
  .product-cover {
    width: 50px;
    height: 50px;
    min-width: 50px;
    border-radius: 4px;
    margin-right: 10px;
  }
  .sku-count {
    font-weight: 600;
    margin-left: 10px;
    white-space: nowrap;
  }
  .el-dropdown-link {
    cursor: pointer;
    color: #409eff;
  }
  .el-icon-arrow-down {
    font-size: 12px;
  }
  .space-nowrap {
    span {
      white-space: nowrap;
    }
  }
</style>
